import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Pressable,
  Image,
  RefreshControl,
  Platform,
  Share,
  Alert,
  Animated,
  LayoutAnimation,
  UIManager,
  Dimensions,
  StatusBar,
  ScrollView,
} from 'react-native';
import {
  useNavigation,
  useRoute,
  useFocusEffect,
} from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Permission } from '../../types/auth';
import PermissionGate from '../../components/shared/PermissionGate';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../hooks/useAuth';
import { SearchBar, SocialShareModal } from '../../components/shared';
import { Tabs } from '../../components/common';
import { Button, Modal as CommonModal } from '../../components/common';
import UserAvatar from '../../components/shared/UserAvatar';
import { useMoodBoards, TabType } from '../../hooks/useMoodBoards';
import MoodBoardListSkeleton from '../../components/custom/moodboard/MoodBoardSkeleton';
import { MoodBoard, moodBoardAPI } from '../../api/mood_boards';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { hexToRgba } from '../../utils/colorUtils';
import {
  VerticalMoodboardCard,
  ThumbnailCard,
  LayoutToggle,
  MoodBoardCardItem,
  CardLayout,
} from '../../components/custom/moodboard';
import { trackButtonClick, trackAnalyticsEvent } from '../../utils/analytics';

// Enable LayoutAnimation on Android
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// Map backend mood board to feed item
const transformMoodBoardToFeedItem = (
  board: MoodBoard,
  updatedBoards?: Map<string, any>
): MoodBoardCardItem => {
  const firstImage =
    board.images && board.images.length > 0 ? board.images[0] : null;
  const mockEmail = board.user_name
    ? `${board.user_name}@example.com`
    : 'user@example.com';

  // Use updated data if available
  const updatedBoard = updatedBoards?.get(board.id.toString());
  const finalBoard = updatedBoard || board;

  return {
    id: board.id.toString(),
    username: finalBoard.user_name || 'Colour Explorer',
    userAvatar: finalBoard.user_profile_image || null,
    userEmail: mockEmail,
    userId: finalBoard.user_id,
    title: finalBoard.name,
    description: finalBoard.description || null,
    location: finalBoard.location || null,
    image: firstImage,
    images: finalBoard.images || [],
    colors: finalBoard.colors.slice(0, 5),
    likes: finalBoard.likes_count,
    tags: finalBoard.tags,
    timestamp: formatTimeAgo(finalBoard.created_at),
    shares: finalBoard.shares_count || 0,
    saved: (finalBoard as any).saves_count || 0,
    isTrending: finalBoard.likes_count > 50,
    is_public: finalBoard.is_public,
    isLiked: finalBoard.is_liked,
    isSaved: (finalBoard as any).is_saved || false,
  };
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
  return `${Math.floor(diffInSeconds / 604800)}w`;
};

const MoodBoardScreen = ({ route: propRoute }: { route?: any } = {}) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const routeName = route.name;
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { hasPermission, isLoggedIn, isGuest } = usePermission();
  const { user } = useAuth();
  const { isMobile, containerWidth } = useResponsiveLayout();
  const { width: screenWidth } = Dimensions.get('window');
  // Start with safe default height, will update on layout
  const [headerHeight, setHeaderHeight] = useState(
    Platform.OS === 'web' ? 220 : 300
  );

  // Check if this screen is inside MainTabs (tab navigation)
  const isInsideMainTabs =
    routeName === 'MoodBoardTabMobile' || routeName === 'MoodBoardTab';

  // Get selectedTab from route params if provided (either from props or useRoute)
  const routeParams = (propRoute || route.params) as
    | {
        selectedTab?: TabType;
        initialTab?: 'HomeTab' | 'MoodBoardTab';
        fromEdit?: boolean;
      }
    | undefined;

  // Performance optimized states - start at final values to prevent flickering
  const [fadeAnim] = useState(new Animated.Value(1));
  const [slideAnim] = useState(new Animated.Value(0));
  const [scaleAnim] = useState(new Animated.Value(1));
  const [selectedTab, setSelectedTab] = useState<TabType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedMoodBoard, setSelectedMoodBoard] = useState<any>(null);
  const [reportingBoardId, setReportingBoardId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'feed' | 'grid'>('feed');
  const [cardLayout, setCardLayout] = useState<CardLayout>('horizontal');
  const [filterByTrending, setFilterByTrending] = useState(false);
  const [isFabHovered, setIsFabHovered] = useState(false);
  const [createButtonHover, setCreateButtonHover] = useState<
    'mobile-nav' | 'web-nav' | 'empty' | null
  >(null);
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'trending'>(
    'recent'
  );
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());
  const [updatedMoodBoards, setUpdatedMoodBoards] = useState<Map<string, any>>(
    new Map()
  );
  const [imageIndices, setImageIndices] = useState<Map<string, number>>(
    new Map()
  );
  const flatListRef = useRef<FlatList>(null);
  const [listHeight, setListHeight] = useState(0);
  const previousSelectedTabRef = useRef<TabType>(selectedTab);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const contentFadeAnim = useRef(new Animated.Value(1)).current;

  const {
    moodBoards,
    filteredBoards,
    currentDataTab,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    error,
    handleRefresh,
    loadMore,
    handleLikeMoodBoard: hookHandleLikeMoodBoard,
    handleSaveMoodBoard: hookHandleSaveMoodBoard,
  } = useMoodBoards({ selectedTab, searchQuery });

  // Initialize selected tab from route params
  useEffect(() => {
    if (routeParams?.fromEdit) {
      setSelectedTab('my');
    } else if (routeParams?.selectedTab) {
      setSelectedTab(routeParams.selectedTab);
    } else if (routeParams?.initialTab === 'MoodBoardTab') {
      setSelectedTab('my');
    }
    // Note: 'all' is the default initial state, no need to set it explicitly
  }, [
    routeParams?.selectedTab,
    routeParams?.initialTab,
    routeParams?.fromEdit,
  ]);

  // Handle route params
  useFocusEffect(
    useCallback(() => {
      const currentParams = (route.params ||
        propRoute?.params) as typeof routeParams;
      if (currentParams?.selectedTab) {
        setSelectedTab(currentParams.selectedTab);
      } else if (currentParams?.fromEdit) {
        setSelectedTab('my');
      }
    }, [route.params, propRoute?.params])
  );

  useEffect(() => {
    if (previousSelectedTabRef.current !== selectedTab) {
      setIsTransitioning(true);
      Animated.timing(contentFadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();

      previousSelectedTabRef.current = selectedTab;
    }
  }, [selectedTab, contentFadeAnim]);

  // Clear transition flag when data is ready and fade in content
  useEffect(() => {
    if (isTransitioning && currentDataTab === selectedTab && !loading) {
      // Add small delay to ensure render is stable
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        // Fade in content smoothly
        Animated.timing(contentFadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning, currentDataTab, selectedTab, loading, contentFadeAnim]);

  // Sync liked/saved posts with mood board data - optimized with useMemo
  useEffect(() => {
    const likedPostIds = new Set<string>();
    const savedPostIds = new Set<string>();
    moodBoards.forEach((board) => {
      if (board.is_liked) {
        likedPostIds.add(board.id.toString());
      }
      if (board.is_saved) {
        savedPostIds.add(board.id.toString());
      }
    });
    setLikedPosts(likedPostIds);
    setSavedPosts(savedPostIds);
  }, [moodBoards]);

  // Memoized handlers for performance
  const handleCreateMoodBoard = useCallback(() => {
    trackButtonClick('Create Moodboard', 'moodboard-screen', {
      button_type: 'create_moodboard',
      screen: 'moodboard',
      location: user ? 'authenticated' : 'guest',
    });

    if (!user) {
      navigation.navigate('Login');
      return;
    }
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    navigation.navigate('MoodBoardDetail', { boardId: 'new' });
  }, [user, navigation]);

  const handleShareMoodBoard = useCallback((moodBoard: any) => {
    setSelectedMoodBoard(moodBoard);
    setShowShareModal(true);
  }, []);

  const handleReportMoodBoard = useCallback(
    (boardId: string) => {
      if (!user) {
        navigation.navigate('Login');
        return;
      }
      setReportingBoardId(boardId);
      setShowReportModal(true);
    },
    [user, navigation]
  );

  const handleTabPress = useCallback(
    (tabId: TabType) => {
      const tabNames: Record<TabType, string> = {
        all: 'For You',
        saved: 'Saved',
        my: 'My Moodboards',
        liked: 'Liked',
      };
      trackButtonClick(tabNames[tabId] || tabId, 'moodboard-screen', {
        button_type: 'tab',
        screen: 'moodboard',
        tab_id: tabId,
        tab_name: tabNames[tabId] || tabId,
      });

      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      setSelectedTab(tabId);

      // Clear route params to prevent them from overriding the manual selection
      if (route.params) {
        navigation.setParams({
          selectedTab: undefined,
          fromEdit: undefined,
        } as any);
      }
    },
    [navigation, route.params]
  );

  const handleLikeMoodBoard = useCallback(
    async (boardId: number) => {
      if (!user) {
        navigation.navigate('Login');
        return;
      }

      const postId = boardId.toString();
      const isCurrentlyLiked = likedPosts.has(postId);

      // Optimistic update for better UX (only visual state, not count)
      setLikedPosts((prev) => {
        const newSet = new Set(prev);
        if (isCurrentlyLiked) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });

      try {
        await hookHandleLikeMoodBoard(boardId);
        // Clear any optimistic updates after successful API call
        setUpdatedMoodBoards((prev) => {
          const newMap = new Map(prev);
          newMap.delete(postId);
          return newMap;
        });
      } catch (error) {
        // Revert optimistic update on error
        setLikedPosts((prev) => {
          const newSet = new Set(prev);
          if (isCurrentlyLiked) {
            newSet.add(postId);
          } else {
            newSet.delete(postId);
          }
          return newSet;
        });

        setUpdatedMoodBoards((prev) => {
          const newMap = new Map(prev);
          newMap.delete(postId);
          return newMap;
        });

        // Like failed silently
      }
    },
    [user, navigation, hookHandleLikeMoodBoard, likedPosts]
  );

  const handleSaveMoodBoard = useCallback(
    async (boardId: number) => {
      if (!user) {
        navigation.navigate('Login');
        return;
      }

      const postId = boardId.toString();
      const isCurrentlySaved = savedPosts.has(postId);

      // Optimistic update for better UX
      setSavedPosts((prev) => {
        const newSet = new Set(prev);
        if (isCurrentlySaved) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });

      try {
        // ✅ Use hook's handleSaveMoodBoard which updates cache optimistically
        await hookHandleSaveMoodBoard(boardId);
        // No need to refresh - cache is updated automatically!
      } catch (error) {
        // Revert optimistic update on error
        setSavedPosts((prev) => {
          const newSet = new Set(prev);
          if (isCurrentlySaved) {
            newSet.add(postId);
          } else {
            newSet.delete(postId);
          }
          return newSet;
        });
        // Save failed
        Alert.alert('Error', 'Failed to save mood board. Please try again.');
      }
    },
    [user, navigation, savedPosts, hookHandleSaveMoodBoard]
  );

  const handleMoodBoardPress = useCallback(
    (boardId: string) => {
      // Navigate to detail screen instead of liking
      navigation.navigate('MoodBoardDetail', { boardId });
    },
    [navigation]
  );

  const handleEditMoodBoard = useCallback(
    (boardId: string) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      navigation.navigate('MoodBoardDetail', { boardId, isEditing: true });
    },
    [navigation]
  );

  const toggleViewMode = useCallback(() => {
    const newViewMode = viewMode === 'feed' ? 'grid' : 'feed';
    trackButtonClick('Thumbnails', 'moodboard-screen', {
      button_type: 'view_mode_toggle',
      screen: 'moodboard',
      previous_view: viewMode,
      new_view: newViewMode,
    });

    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setViewMode(newViewMode);
  }, [viewMode]);

  const toggleCardLayout = useCallback(() => {
    const newLayout = cardLayout === 'horizontal' ? 'thumbnail' : 'horizontal';
    trackButtonClick('Layout Toggle', 'moodboard-screen', {
      button_type: 'layout_toggle',
      screen: 'moodboard',
      previous_layout: cardLayout,
      new_layout: newLayout,
    });

    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setCardLayout(newLayout);
  }, [cardLayout]);

  // Handle search with tracking
  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);

    // Track search when user types (debounced tracking could be added if needed)
    if (text.length > 0) {
      trackAnalyticsEvent({
        event: 'search',
        search_term: text,
        screen: 'moodboard',
        search_location: 'moodboard-screen',
      });
    }
  }, []);

  const handleNextImage = useCallback(
    (boardId: string, totalImages: number, e?: any) => {
      if (e) {
        e.stopPropagation();
      }
      setImageIndices((prev) => {
        const currentIndex = prev.get(boardId) || 0;
        const nextIndex = (currentIndex + 1) % totalImages;
        const newMap = new Map(prev);
        newMap.set(boardId, nextIndex);
        return newMap;
      });
    },
    []
  );

  const handlePrevImage = useCallback(
    (boardId: string, totalImages: number, e?: any) => {
      if (e) {
        e.stopPropagation();
      }
      setImageIndices((prev) => {
        const currentIndex = prev.get(boardId) || 0;
        const prevIndex = (currentIndex - 1 + totalImages) % totalImages;
        const newMap = new Map(prev);
        newMap.set(boardId, prevIndex);
        return newMap;
      });
    },
    []
  );

  // Optimized search bar
  const renderSearchBar = () => (
    <Animated.View
      style={[
        styles.searchContainer,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      <SearchBar
        value={searchQuery}
        onChangeText={handleSearchChange}
        placeholder="Search Moodboards..."
        showClearButton={true}
        style={styles.searchBar}
      />
    </Animated.View>
  );

  const renderTabs = () => {
    const tabs = [
      { id: 'all', name: isMobile ? 'For You' : 'For You', icon: 'sparkles' },
      { id: 'saved', name: 'Saved', icon: 'bookmark' },
      ...(user
        ? [
            {
              id: 'my',
              name: isMobile ? 'My' : 'My Mood Boards',
              icon: 'person',
            },
          ]
        : []),
      ...(user ? [{ id: 'liked', name: 'Liked', icon: 'heart' }] : []),
    ];

    return (
      <Animated.View
        style={[
          styles.tabsContainer,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        {isMobile ? (
          <>
            <View style={styles.tabsRightActionsMobile}>
              <View style={styles.searchInNavbar}>
                <SearchBar
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                  placeholder="Search Moodboards..."
                  showClearButton={true}
                  style={styles.searchBarInNavbar}
                />
              </View>
              {user && (
                <Pressable
                  style={({ pressed }) => [
                    styles.createButtonInTabs,
                    {
                      backgroundColor:
                        createButtonHover === 'mobile-nav'
                          ? colors.primary + 'E6'
                          : colors.primary,
                      borderColor: colors.primary,
                    },
                    pressed && {
                      backgroundColor: colors.primary + 'CC',
                      opacity: 0.95,
                    },
                  ]}
                  onPress={handleCreateMoodBoard}
                  onHoverIn={() => setCreateButtonHover('mobile-nav')}
                  onHoverOut={() => setCreateButtonHover(null)}
                >
                  <Ionicons name="add-circle" size={18} color={palette.white} />
                  <Text
                    style={[
                      styles.createButtonTextInTabs,
                      { color: palette.white },
                    ]}
                    numberOfLines={1}
                  >
                    Create
                  </Text>
                </Pressable>
              )}
            </View>
            <View style={styles.tabsRowMobile}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.tabsScrollContent}
                style={styles.tabsScrollView}
              >
                <Tabs
                  tabs={tabs}
                  selectedTab={selectedTab}
                  onTabPress={(tabId) => handleTabPress(tabId as TabType)}
                  variant="compact"
                  size={isMobile ? 'dense' : 'default'}
                />
              </ScrollView>
            </View>
          </>
        ) : (
          <View style={styles.tabsRow}>
            <Tabs
              tabs={tabs}
              selectedTab={selectedTab}
              onTabPress={(tabId) => handleTabPress(tabId as TabType)}
              variant="compact"
            />
            <View style={styles.tabsRightActions}>
              {/* Search Bar beside Layout Toggle */}
              <View style={styles.searchInNavbar}>
                <SearchBar
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                  placeholder="Search Moodboards..."
                  showClearButton={true}
                  style={styles.searchBarInNavbar}
                />
              </View>
              {/* Layout Toggle */}
              <LayoutToggle
                layout={cardLayout}
                onLayoutChange={(layout) => {
                  trackButtonClick('Layout Toggle', 'moodboard-screen', {
                    button_type: 'layout_toggle',
                    screen: 'moodboard',
                    previous_layout: cardLayout,
                    new_layout: layout,
                  });

                  LayoutAnimation.configureNext(
                    LayoutAnimation.Presets.easeInEaseOut
                  );
                  setCardLayout(layout);
                }}
                showLabels={true}
                compact={false}
              />
              {user && (
                <Pressable
                  style={({ pressed }) => [
                    styles.createButtonInTabs,
                    {
                      borderColor: colors.primary,
                      backgroundColor:
                        createButtonHover === 'web-nav'
                          ? colors.primary + '18'
                          : 'transparent',
                    },
                    pressed && { backgroundColor: colors.primary + '25' },
                  ]}
                  onPress={handleCreateMoodBoard}
                  onHoverIn={() => setCreateButtonHover('web-nav')}
                  onHoverOut={() => setCreateButtonHover(null)}
                >
                  <Ionicons name="add" size={14} color={colors.primary} />
                  <Text
                    style={[
                      styles.createButtonTextInTabs,
                      { color: colors.primary },
                    ]}
                  >
                    Create Moodboard
                  </Text>
                </Pressable>
              )}
            </View>
          </View>
        )}
      </Animated.View>
    );
  };

  // Thumbnail card design - compact grid view
  const renderThumbnailCard = useCallback(
    ({ item, index }: { item: MoodBoardCardItem; index: number }) => {
      const currentImageIndex = imageIndices.get(item.id) || 0;
      const cardItem: MoodBoardCardItem = {
        ...item,
        isLiked: likedPosts.has(item.id),
      };

      return (
        <ThumbnailCard
          item={cardItem}
          actions={{
            onPress: handleMoodBoardPress,
            onLike: (id) => handleLikeMoodBoard(parseInt(id)),
            onShare: handleShareMoodBoard,
            onSave: () => {},
          }}
          imageNavigation={{
            currentIndex: currentImageIndex,
            totalImages: item.images.length,
            onNext: () => handleNextImage(item.id, item.images.length),
            onPrev: () => handlePrevImage(item.id, item.images.length),
            showCount: true,
          }}
        />
      );
    },
    [
      imageIndices,
      likedPosts,
      handleMoodBoardPress,
      handleLikeMoodBoard,
      handleShareMoodBoard,
      handleNextImage,
      handlePrevImage,
    ]
  );

  // Modern feed card design
  const renderFeedCard = useCallback(
    ({ item, index }: { item: MoodBoardCardItem; index: number }) => {
      const currentImageIndex = imageIndices.get(item.id) || 0;
      const cardItem: MoodBoardCardItem = {
        ...item,
        isLiked: likedPosts.has(item.id),
        isSaved: savedPosts.has(item.id),
      };

      const handleNextImageSet = () => {
        const currentSetIndex = Math.floor(currentImageIndex / 3);
        const newIndex = Math.min(
          item.images.length - 1,
          (currentSetIndex + 1) * 3
        );
        setImageIndices((prev) => {
          const newMap = new Map(prev);
          newMap.set(item.id, newIndex);
          return newMap;
        });
      };

      const handlePrevImageSet = () => {
        const currentSetIndex = Math.floor(currentImageIndex / 3);
        const newIndex = Math.max(0, (currentSetIndex - 1) * 3);
        setImageIndices((prev) => {
          const newMap = new Map(prev);
          newMap.set(item.id, newIndex);
          return newMap;
        });
      };

      return (
        <VerticalMoodboardCard
          item={cardItem}
          actions={{
            onPress: handleMoodBoardPress,
            onLike: (id) => handleLikeMoodBoard(parseInt(id)),
            onShare: handleShareMoodBoard,
            onEdit: handleEditMoodBoard,
            onSave: (id) => handleSaveMoodBoard(parseInt(id)),
            onReport: handleReportMoodBoard,
          }}
          imageNavigation={{
            currentIndex: currentImageIndex,
            totalImages: item.images.length,
            onNext: () => handleNextImage(item.id, item.images.length),
            onPrev: () => handlePrevImage(item.id, item.images.length),
            showCount: true,
          }}
          showEditButton={!!user && item.userId === parseInt(user.id)}
          currentUserId={user?.id}
        />
      );
    },
    [
      imageIndices,
      likedPosts,
      savedPosts,
      handleMoodBoardPress,
      handleLikeMoodBoard,
      handleShareMoodBoard,
      handleEditMoodBoard,
      handleSaveMoodBoard,
      handleReportMoodBoard,
      handleNextImage,
      handlePrevImage,
      user,
    ]
  );

  // Memoized keyExtractor for FlatList
  const keyExtractor = useCallback(
    (item: MoodBoardCardItem, index: number) => `${item.id || index}-moodboard`,
    []
  );

  // Memoized renderItem for FlatList
  const renderItem = useCallback(
    ({ item, index }: { item: MoodBoardCardItem; index: number }) => {
      if (cardLayout === 'thumbnail') {
        return (
          <View style={styles.thumbnailCardWrapper}>
            {renderThumbnailCard({ item, index })}
          </View>
        );
      }
      return (
        <View
          style={{
            marginTop: index === 0 ? 0 : Platform.OS === 'web' ? 20 : 0,
            marginBottom: 0,
            marginHorizontal: Platform.OS === 'web' ? 'auto' : 0,
            width: Platform.OS === 'web' ? 'auto' : '100%',
            maxWidth: Platform.OS === 'web' ? '100%' : '100%',
            minWidth: Platform.OS === 'web' ? 0 : '100%',
            paddingHorizontal: 0,
            paddingTop: 0,
            alignSelf: Platform.OS === 'web' ? 'center' : 'stretch',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {renderFeedCard({ item, index })}
        </View>
      );
    },
    [cardLayout, renderThumbnailCard, renderFeedCard]
  );

  // Optimized empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <View style={styles.emptyContent}>
        {selectedTab === 'my' && user ? (
          <Image
            source={require('../../../assets/illustrations/girl-canvas-palette.png')}
            style={styles.paintingIcon}
            resizeMode="contain"
          />
        ) : selectedTab === 'liked' && user ? (
          <Image
            source={require('../../../assets/illustrations/like-hands.png')}
            style={styles.paintingIcon}
            resizeMode="contain"
          />
        ) : selectedTab === 'saved' && user ? (
          <Image
            source={require('../../../assets/illustrations/high-five.png')}
            style={styles.paintingIcon}
            resizeMode="contain"
          />
        ) : (
          <Image
            source={require('../../../assets/illustrations/painting.png')}
            style={styles.paintingIcon}
            resizeMode="contain"
          />
        )}
        <Text
          style={[
            styles.emptyTitle,
            isMobile && styles.emptyTitleMobile,
            { color: colors.text },
          ]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {selectedTab === 'my' && user
            ? 'Start Your Moodboard'
            : selectedTab === 'liked' && user
              ? 'Collect Your Favorites'
              : selectedTab === 'saved' && user
                ? 'No Saved Moodboard Yet'
                : 'No Moodboard Found'}
        </Text>
        <Text
          style={[
            styles.emptySubtitle,
            isMobile && styles.emptySubtitleMobile,
            { color: colors.secondary },
          ]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {selectedTab === 'my' && user
            ? 'Create your first moodboard and share your unique palette!'
            : selectedTab === 'liked' && user
              ? 'Like some moodboards to build your inspiration collection'
              : selectedTab === 'saved' && user
                ? 'Save moodboards you love to access them later!'
                : searchQuery
                  ? 'Try exploring different color themes'
                  : 'Be the first to share a beautiful moodboard!'}
        </Text>
        {((selectedTab === 'my' && user) || selectedTab === 'all') && (
          <Pressable
            style={({ pressed }) => [
              styles.createButton,
              {
                backgroundColor:
                  createButtonHover === 'empty'
                    ? colors.primary + 'E6'
                    : colors.primary,
                shadowColor: colors.primary,
                transform: [
                  { scale: createButtonHover === 'empty' ? 1.03 : 1 },
                ],
              },
              pressed && {
                backgroundColor: colors.primary + 'CC',
                opacity: 0.95,
                transform: [{ scale: 0.98 }],
              },
            ]}
            onPress={handleCreateMoodBoard}
            onHoverIn={() => setCreateButtonHover('empty')}
            onHoverOut={() => setCreateButtonHover(null)}
          >
            <Ionicons name="add-circle" size={20} color="white" />
            <Text style={styles.createButtonText}>Create Moodboard</Text>
          </Pressable>
        )}
      </View>
    </View>
  );

  const prepareFeedData = useMemo(() => {
    let boardsToShow = filteredBoards || [];
    if (selectedTab === 'saved' && currentDataTab === selectedTab) {
      boardsToShow = (filteredBoards || []).filter(
        (board) =>
          board.is_saved === true || savedPosts.has(board.id.toString())
      );
    }

    return boardsToShow.map((board) =>
      transformMoodBoardToFeedItem(board, updatedMoodBoards)
    );
  }, [
    filteredBoards,
    selectedTab,
    currentDataTab,
    savedPosts,
    updatedMoodBoards,
  ]);

  useEffect(() => {
    if (
      !loading &&
      !refreshing &&
      prepareFeedData &&
      prepareFeedData.length > 0 &&
      hasMore &&
      !loadingMore &&
      listHeight > 0
    ) {
      const timer = setTimeout(() => {}, 100);
      return () => clearTimeout(timer);
    }
  }, [
    loading,
    refreshing,
    prepareFeedData?.length,
    hasMore,
    loadingMore,
    listHeight,
  ]);

  const handleEndReached = useCallback(() => {
    if (hasMore && !loadingMore && !loading && !refreshing) {
      loadMore();
    }
  }, [hasMore, loadingMore, loading, refreshing, loadMore]);

  const autoLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckedContentHeightRef = useRef<number>(0);

  const handleContentSizeChange = useCallback(
    (contentWidth: number, contentHeight: number) => {
      if (Math.abs(contentHeight - lastCheckedContentHeightRef.current) < 50) {
        return;
      }
      lastCheckedContentHeightRef.current = contentHeight;

      if (autoLoadTimerRef.current) {
        clearTimeout(autoLoadTimerRef.current);
        autoLoadTimerRef.current = null;
      }

      if (
        prepareFeedData &&
        prepareFeedData.length > 0 &&
        hasMore &&
        !loadingMore &&
        !loading &&
        !refreshing
      ) {
        const { height: viewportHeight } = Dimensions.get('window');
        const headerHeightWithInsets =
          headerHeight + (Platform.OS === 'web' ? 0 : insets.top);
        const availableHeight = viewportHeight - headerHeightWithInsets;

        const shouldLoad =
          contentHeight < availableHeight - 100 || prepareFeedData.length < 3;

        if (shouldLoad) {
          const dataLengthWhenDecided = prepareFeedData.length;

          autoLoadTimerRef.current = setTimeout(() => {
            const stillNeedsLoad =
              prepareFeedData &&
              prepareFeedData.length <= dataLengthWhenDecided + 1 && // Allow for 1 new item that might have rendered
              hasMore;

            if (
              hasMore &&
              !loadingMore &&
              !loading &&
              !refreshing &&
              stillNeedsLoad
            ) {
              loadMore();
            }
            autoLoadTimerRef.current = null;
          }, 300);
        }
      }
    },
    [
      prepareFeedData,
      hasMore,
      loadingMore,
      loading,
      refreshing,
      headerHeight,
      insets.top,
      loadMore,
    ]
  );

  const handleListLayout = useCallback(
    (event: any) => {
      const { height } = event.nativeEvent.layout;
      if (height > 0 && height !== listHeight) {
        setListHeight(height);
      }
    },
    [listHeight]
  );

  const renderListFooter = useCallback(() => {
    if (!loadingMore) return null;

    return (
      <View style={styles.loadingMoreContainer}>
        <Text style={[styles.loadingMoreText, { color: colors.secondary }]}>
          Loading more...
        </Text>
      </View>
    );
  }, [loadingMore, colors.secondary]);

  const renderListEmpty = useCallback(() => {
    if (loading || currentDataTab !== selectedTab || isTransitioning) {
      return null;
    }

    if (error) {
      return (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={48} color={colors.billing} />
          <Text style={[styles.errorText, { color: colors.billing }]}>
            {error}
          </Text>
          <Button
            title="Try Again"
            onPress={handleRefresh}
            style={{ marginTop: 16 }}
            trackClick={true}
            trackingLocation="moodboard-screen"
            trackingData={{
              button_name: 'Try Again',
              button_type: 'try_again',
              screen: 'moodboard',
            }}
          />
        </View>
      );
    }

    if (
      !loading &&
      !refreshing &&
      !isTransitioning &&
      currentDataTab === selectedTab &&
      (!prepareFeedData || prepareFeedData.length === 0)
    ) {
      return renderEmptyState();
    }

    return null;
  }, [
    loading,
    error,
    prepareFeedData?.length,
    refreshing,
    currentDataTab,
    selectedTab,
    isTransitioning,
    colors,
    cardLayout,
    handleRefresh,
  ]);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />

      <View
        style={[
          styles.fixedHeader,
          {
            backgroundColor: colors.background,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
          },
        ]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(Math.ceil(height) + (Platform.OS === 'web' ? 6 : 8));
        }}
      >
        {renderTabs()}
      </View>

      <View
        style={[
          Platform.OS === 'web'
            ? [styles.webContentContainer, { top: headerHeight }]
            : [
                styles.scrollableMoodBoards,
                {
                  marginTop: headerHeight + insets.top,
                  paddingTop: 0,
                },
              ],
        ]}
      >
        {loading || currentDataTab !== selectedTab || isTransitioning ? (
          <View style={styles.transitionSkeletonContainer}>
            <MoodBoardListSkeleton count={4} layout={cardLayout} />
          </View>
        ) : (
          <Animated.View style={{ flex: 1, opacity: contentFadeAnim }}>
            <FlatList
              ref={flatListRef}
              data={currentDataTab === selectedTab ? prepareFeedData : []}
              keyExtractor={keyExtractor}
              extraData={{ likedPosts, savedPosts, imageIndices, cardLayout }}
              onLayout={handleListLayout}
              renderItem={renderItem}
              numColumns={
                cardLayout === 'thumbnail' && Platform.OS === 'web' ? 3 : 1
              }
              columnWrapperStyle={
                cardLayout === 'thumbnail' && Platform.OS === 'web'
                  ? styles.thumbnailGridRow
                  : undefined
              }
              key={cardLayout === 'thumbnail' ? 'thumbnail-grid' : 'feed-list'}
              ListEmptyComponent={renderListEmpty}
              ListFooterComponent={renderListFooter}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.3}
              onContentSizeChange={handleContentSizeChange}
              scrollEnabled={prepareFeedData.length > 0}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={handleRefresh}
                  colors={[colors.primary]}
                  tintColor={colors.primary}
                />
              }
              showsVerticalScrollIndicator={false}
              contentContainerStyle={[
                Platform.OS === 'web'
                  ? styles.webScrollContent
                  : [
                      styles.scrollableMoodBoardsContent,
                      {
                        paddingTop: 0,
                        paddingBottom: 100,
                      },
                    ],

                !prepareFeedData || prepareFeedData.length === 0
                  ? { flexGrow: 1 }
                  : {},
              ]}
              style={
                Platform.OS === 'web'
                  ? styles.webScrollView
                  : styles.scrollableMoodBoards
              }
              removeClippedSubviews={Platform.OS !== 'web'}
              maxToRenderPerBatch={10}
              windowSize={10}
              initialNumToRender={5}
              maintainVisibleContentPosition={null}
              scrollEventThrottle={400}
            />
          </Animated.View>
        )}
      </View>

      {/* Fixed Floating Action Button - Hidden */}
      {false && (
        <PermissionGate
          permission={Permission.CREATE_MOODBOARD}
          showLoginPrompt={true}
        >
          <Animated.View
            style={[
              styles.floatingActionButton,
              {
                opacity: fadeAnim,
                transform: [{ scale: scaleAnim }],
              },
            ]}
          >
            <Pressable
              style={({ pressed }) => [
                styles.fabButton,
                {
                  backgroundColor: isFabHovered
                    ? colors.primary
                    : colors.theme_color,
                },
                pressed && { opacity: 0.9 },
              ]}
              onPress={handleCreateMoodBoard}
              onHoverIn={() => setIsFabHovered(true)}
              onHoverOut={() => setIsFabHovered(false)}
            >
              <Ionicons name="add" size={24} color="white" />
            </Pressable>
          </Animated.View>
        </PermissionGate>
      )}

      {/* Social Share Modal */}
      {selectedMoodBoard && (
        <SocialShareModal
          visible={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedMoodBoard(null);
          }}
          moodBoard={selectedMoodBoard}
          onShared={(updated) => {
            setUpdatedMoodBoards((prev) => {
              const newMap = new Map(prev);
              const postId = updated.id.toString();
              const current =
                newMap.get(postId) ||
                moodBoards.find((b) => b.id.toString() === postId);
              if (current) {
                newMap.set(postId, {
                  ...current,
                  shares_count: updated.shares_count,
                });
              }
              return newMap;
            });
          }}
        />
      )}

      {/* Report Modal */}
      <ReportModal
        visible={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportingBoardId(null);
        }}
        boardId={reportingBoardId}
        onReport={async (reason: string) => {
          if (!reportingBoardId) return;
          try {
            await moodBoardAPI.report(parseInt(reportingBoardId), reason);
            Alert.alert(
              'Report Submitted',
              'Thank you for reporting this content. We will review it shortly.'
            );
            setShowReportModal(false);
            setReportingBoardId(null);
          } catch (error: any) {
            Alert.alert(
              'Error',
              error.response?.data?.detail ||
                'Failed to submit report. Please try again.'
            );
          }
        }}
      />
    </View>
  );
};

interface ReportModalProps {
  visible: boolean;
  onClose: () => void;
  boardId: string | null;
  onReport: (reason: string) => Promise<void>;
}

const ReportModal: React.FC<ReportModalProps> = ({
  visible,
  onClose,
  onReport,
}) => {
  const { colors } = useTheme();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const reportReasons = [
    'Inappropriate content',
    'Spam or misleading',
    'Harassment or bullying',
    'Copyright violation',
    'Violence or dangerous content',
    'Other',
  ];

  const handleSubmit = async () => {
    if (!selectedReason) {
      Alert.alert(
        'Please select a reason',
        'You must select a reason for reporting this content.'
      );
      return;
    }
    setLoading(true);
    try {
      await onReport(selectedReason);
      setSelectedReason('');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonModal visible={visible} onClose={onClose} title="Report Content">
      <Text style={[styles.reportModalSubtitle, { color: colors.secondary }]}>
        Please select a reason for reporting this content:
      </Text>

      <View style={styles.reportReasonsList}>
        {reportReasons.map((reason) => (
          <TouchableOpacity
            key={reason}
            style={[
              styles.reportReasonItem,
              {
                backgroundColor:
                  selectedReason === reason
                    ? colors.primary + '20'
                    : colors.background,
                borderColor:
                  selectedReason === reason ? colors.primary : colors.border,
              },
            ]}
            onPress={() => setSelectedReason(reason)}
            activeOpacity={0.7}
          >
            <Ionicons
              name={
                selectedReason === reason
                  ? 'radio-button-on'
                  : 'radio-button-off'
              }
              size={20}
              color={
                selectedReason === reason ? colors.primary : colors.secondary
              }
            />
            <Text style={[styles.reportReasonText, { color: colors.text }]}>
              {reason}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.reportModalActions}>
        <TouchableOpacity
          style={[
            styles.reportModalCancelButton,
            { borderColor: colors.border },
          ]}
          onPress={onClose}
          disabled={loading}
        >
          <Text style={[styles.reportModalCancelText, { color: colors.text }]}>
            Cancel
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.reportModalSubmitButton,
            {
              backgroundColor: selectedReason ? colors.primary : colors.border,
              opacity: selectedReason ? 1 : 0.5,
            },
          ]}
          onPress={handleSubmit}
          disabled={!selectedReason || loading}
        >
          {loading ? (
            <Text
              style={[
                styles.reportModalSubmitText,
                { color: selectedReason ? colors.background : colors.text },
              ]}
            >
              Submitting...
            </Text>
          ) : (
            <Text
              style={[
                styles.reportModalSubmitText,
                { color: selectedReason ? colors.background : colors.text },
              ]}
            >
              Submit Report
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </CommonModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },

  fixedHeader: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },

  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },

  viewModeButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },

  layoutToggleButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },

  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 24 : 28,
    paddingVertical: Platform.OS === 'web' ? 14 : 16,
    borderRadius: Platform.OS === 'web' ? 12 : 14,
    gap: Platform.OS === 'web' ? 8 : 10,
    minHeight: Platform.OS === 'web' ? 48 : 54,
    shadowOffset: Platform.OS === 'web' ? undefined : { width: 0, height: 4 },
    shadowOpacity: Platform.OS === 'web' ? undefined : 0.25,
    shadowRadius: Platform.OS === 'web' ? undefined : 8,
    elevation: Platform.OS === 'web' ? undefined : 4,
  },

  createButtonIconContainer: {
    marginRight: Platform.OS === 'web' ? 0 : 4,
  },

  createButtonText: {
    color: palette.white,
    fontSize: Platform.OS === 'web' ? 16 : 17,
    fontWeight: '600',
    letterSpacing: Platform.OS === 'web' ? 0.2 : 0.5,
  },

  searchContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },

  searchBar: {
    borderRadius: 12,
  },

  tabsContainer: {
    paddingHorizontal: Platform.OS === 'web' ? 16 : 0,
    paddingTop: Platform.OS === 'web' ? 8 : 12,
    paddingBottom: Platform.OS === 'web' ? 8 : 8,
    marginTop: Platform.OS === 'web' ? 0 : 0,
    marginBottom: Platform.OS === 'web' ? 0 : 0,
    backgroundColor: Platform.OS === 'web' ? 'transparent' : undefined,
  },

  tabsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  tabsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 0,
    paddingBottom: 0,
    maxWidth: '100%',
  },
  tabsScrollView: {
    flexGrow: 0,
    maxWidth: '100%',
  },
  tabsScrollContent: {
    paddingHorizontal: 12,
    alignItems: 'center',
  },

  searchInNavbar: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 200 : 100,
    maxWidth: Platform.OS === 'web' ? 500 : '100%',
    marginRight: Platform.OS === 'web' ? 6 : 12,
    marginLeft: Platform.OS === 'web' ? 6 : 0,
  },
  searchBarInNavbar: {
    borderRadius: Platform.OS === 'web' ? 8 : 10,
    height: Platform.OS === 'web' ? 32 : 40,
  },
  tabsRightActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabsRightActionsMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
    paddingHorizontal: 16,
    paddingTop: 4,
  },

  layoutToggleInTabs: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: 'transparent',
    borderWidth: 1,
    gap: 6,
  },

  layoutToggleText: {
    fontSize: 13,
    fontWeight: '600',
  },

  createButtonInTabs: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 12 : 16,
    paddingVertical: Platform.OS === 'web' ? 6 : 10,
    borderRadius: Platform.OS === 'web' ? 8 : 10,
    backgroundColor: Platform.OS === 'web' ? 'transparent' : undefined,
    borderWidth: Platform.OS === 'web' ? 1.5 : 0,
    gap: Platform.OS === 'web' ? 4 : 5,
    flexShrink: 0,
    minHeight: Platform.OS === 'web' ? undefined : 40,
    minWidth: Platform.OS === 'web' ? undefined : 90,
    shadowOffset: Platform.OS === 'web' ? undefined : { width: 0, height: 2 },
    shadowOpacity: Platform.OS === 'web' ? undefined : 0.2,
    shadowRadius: Platform.OS === 'web' ? undefined : 3,
    elevation: Platform.OS === 'web' ? undefined : 3,
  },

  createButtonTextInTabs: {
    fontSize: Platform.OS === 'web' ? 11 : 14,
    fontWeight: '600',
    flexShrink: 0,
    letterSpacing: Platform.OS === 'web' ? 0 : 0.2,
  },

  listContainer: {
    paddingHorizontal: Platform.OS === 'web' ? 16 : 0,
    paddingBottom: 100,
  },

  gridRow: {
    justifyContent: 'space-between',
  },

  feedCardContainer: {
    marginBottom: Platform.OS === 'web' ? 12 : 0,
    width: Platform.OS === 'web' ? '70%' : '100%',
    maxWidth: Platform.OS === 'web' ? '70%' : '100%',
    minWidth: 0,
    alignSelf: Platform.OS === 'web' ? 'flex-start' : 'stretch',
    marginHorizontal: 0,
    paddingHorizontal: 0,
    alignContent: 'stretch',
  },

  feedCard: {
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    marginHorizontal: 0,
    alignSelf: 'stretch',
    marginBottom: Platform.OS === 'web' ? 0 : 1,
  },

  cardHorizontalLayout: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 200,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },

  cardVerticalLayout: {
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },

  cardImageSection: {
    padding: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
    width: '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for images on web, full width on mobile
    alignSelf: 'stretch',
  },

  cardImageSectionHorizontal: {
    width: Platform.OS === 'web' ? '60%' : '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for horizontal images on web, full width on mobile
    paddingHorizontal: Platform.OS === 'web' ? 12 : 0,
    paddingVertical: Platform.OS === 'web' ? 12 : 0,
    alignSelf: 'stretch',
  },

  cardImageSectionVertical: {
    width: '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for vertical images on web, full width on mobile
    alignSelf: 'stretch',
  },

  cardImageContainer: {
    width: '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for image container on web, full width on mobile
    borderRadius: Platform.OS === 'web' ? 12 : 0,
    overflow: 'hidden',
  },

  cardImageContainerHorizontal: {
    height: 200,
    flexDirection: 'row',
    gap: 4,
  },

  cardImageContainerVertical: {
    height: Platform.OS === 'web' ? 250 : 300,
    aspectRatio: Platform.OS === 'web' ? 16 / 9 : undefined,
    width: '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for vertical image container on web, full width on mobile
    alignSelf: 'stretch',
  },

  cardSingleImage: {
    width: '100%',
    height: '100%',
    minWidth: Platform.OS === 'web' ? 200 : 0, // Fixed minimum width for single image on web, full width on mobile
  },

  cardImageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    height: '100%',
    gap: 2,
  },

  cardImageGridHorizontal: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },

  cardHorizontalImageItem: {
    flex: 1,
    minWidth: 120, // Fixed minimum width for horizontal image items
    borderRadius: 8,
    overflow: 'hidden',
  },

  cardHorizontalImage: {
    width: '100%',
    height: '100%',
  },

  cardGridImageContainer: {
    width: '48%',
    height: '48%',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },

  cardGridImage: {
    width: '100%',
    height: '100%',
  },

  cardImageWrapper: {
    position: 'relative',
  },

  cardNavButton: {
    position: 'absolute',
    left: 12,
    top: '50%',
    marginTop: -24,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 24,
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },

  cardNavButtonRight: {
    left: 'auto',
    right: 12,
  },

  cardImageCountBadge: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    backgroundColor: 'rgba(0,0,0,0.7)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    zIndex: 10,
  },

  cardImageCountText: {
    color: palette.white,
    fontSize: 12,
    fontWeight: '600',
  },

  cardContentSection: {
    flex: 1,
    padding: Platform.OS === 'web' ? 12 : 0,
    paddingHorizontal: Platform.OS === 'web' ? 16 : 0,
    paddingVertical: Platform.OS === 'web' ? 12 : 16,
    paddingRight: Platform.OS === 'web' ? 16 : 0,
    width: Platform.OS === 'web' ? '40%' : '100%',
    maxWidth: Platform.OS === 'web' ? '40%' : '100%',
    minWidth: Platform.OS === 'web' ? 160 : 0,
    overflow: 'visible',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    boxSizing: 'border-box' as any,
  },

  cardContentSectionVertical: {
    paddingTop: Platform.OS === 'web' ? 16 : 12,
    paddingHorizontal: Platform.OS === 'web' ? 16 : 12,
    paddingRight: Platform.OS === 'web' ? 16 : 12,
    paddingBottom: Platform.OS === 'web' ? 16 : 12,
    width: '100%',
    maxWidth: '100%',
    overflow: 'visible',
    alignSelf: 'stretch',
    boxSizing: 'border-box' as any,
  },

  // Card Header
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 0,
    paddingBottom: 12,
    paddingHorizontal: 0,
    flexWrap: 'wrap',
    gap: 6,
    width: '100%',
    maxWidth: '100%',
    marginBottom: 8,
  },

  cardHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },

  editButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },

  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },

  userInfo: {
    marginLeft: 6,
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },

  username: {
    fontSize: Platform.OS === 'web' ? 11 : 14,
    fontWeight: '600',
    marginBottom: Platform.OS === 'web' ? 1 : 2,
    flexShrink: 1,
    flexWrap: 'wrap',
  },

  timestamp: {
    fontSize: Platform.OS === 'web' ? 10 : 12,
    opacity: 0.6,
  },

  trendingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    borderRadius: 12,
    gap: 4,
  },

  trendingText: {
    fontSize: 10,
    fontWeight: '600',
    color: palette.brightRed,
  },

  // Card Content
  cardContent: {
    paddingHorizontal: Platform.OS === 'web' ? 12 : 12,
    paddingBottom: Platform.OS === 'web' ? 8 : 12,
  },

  cardTitle: {
    fontSize: Platform.OS === 'web' ? 14 : 16,
    fontWeight: '700',
    marginBottom: Platform.OS === 'web' ? 6 : 8,
    marginTop: Platform.OS === 'web' ? 2 : 0,
    flexWrap: 'wrap',
    flexShrink: 1,
    paddingRight: 0,
    lineHeight: Platform.OS === 'web' ? 18 : 20,
  },

  cardDescription: {
    fontSize: Platform.OS === 'web' ? 11 : 14,
    lineHeight: Platform.OS === 'web' ? 16 : 20,
    opacity: 0.75,
    marginBottom: Platform.OS === 'web' ? 8 : 10,
    flexWrap: 'wrap',
    flexShrink: 1,
    paddingRight: 0,
  },

  cardColorsSection: {
    marginBottom: 8,
  },

  cardTagsSection: {
    marginBottom: 8,
  },

  cardActionsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    gap: Platform.OS === 'web' ? 16 : 20,
    marginTop: Platform.OS === 'web' ? 8 : 0,
    paddingTop: Platform.OS === 'web' ? 12 : 12,
    paddingHorizontal: Platform.OS === 'web' ? 16 : 12,
    paddingBottom: Platform.OS === 'web' ? 0 : 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
    flexWrap: 'nowrap',
    width: '100%',
  },

  cardActionsSectionInline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingTop: 12,
    paddingBottom: 12,
    flexWrap: 'nowrap',
    flexShrink: 0,
    width: '100%',
  },

  cardMainContent: {
    flex: 1,
    minHeight: 0,
  },

  cardBottomSection: {
    marginTop: 'auto',
    flexShrink: 0,
    paddingTop: 8,
  },

  cardColorsSectionFixed: {
    marginBottom: 0,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },

  // Visual Content
  visualContent: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },

  imageGrid: {
    flexDirection: 'row',
    height: Platform.OS === 'web' ? 280 : 280,
    gap: 2,
  },

  imageContainer: {
    width: '24%',
    aspectRatio: 1,
    borderRadius: Platform.OS === 'web' ? 12 : 12,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: 'rgba(0,0,0,0.05)',
  },

  feedImage: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
  },

  moreImagesOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  moreImagesText: {
    color: palette.white,
    fontSize: 12,
    fontWeight: '600',
  },

  emptyImageContainer: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    borderStyle: 'dashed',
  },

  emptyImagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },

  // Color Palette
  colorPalette: {
    flexDirection: 'row',
    height: 40,
    borderRadius: 8,
    overflow: 'hidden',
  },

  colorPaletteCircles: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },

  colorSwatch: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  colorCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },

  colorHex: {
    fontSize: 8,
    fontWeight: '600',
    color: palette.white,
    textShadow: '0 1px 1px rgba(0,0,0,0.5)',
  },

  // Tags
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },

  tag: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
  },

  tagText: {
    fontSize: Platform.OS === 'web' ? 9 : 12,
    fontWeight: '500',
  },

  moreTagsText: {
    fontSize: 10,
    opacity: 0.6,
    alignSelf: 'center',
  },

  // Engagement Bar
  engagementBar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderTopWidth: 1,
    gap: 16,
  },

  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    gap: 4,
    flexShrink: 0,
    minWidth: 60,
  },

  actionText: {
    fontSize: Platform.OS === 'web' ? 9 : 13,
    fontWeight: '600',
  },

  emptyContainer: {
    flex: 1,
    margin: 16,
  },

  emptyContent: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: Platform.OS === 'web' ? 48 : 32,
    paddingTop: Platform.OS === 'web' ? 60 : 40,
  },

  emptyIconContainer: {
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(124, 58, 237, 0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 40,
    borderWidth: 1,
    borderColor: 'rgba(124, 58, 237, 0.1)',
  },

  paintingIcon: {
    width: 320,
    height: 300,
    marginBottom: 10,
  },

  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 0,
    marginBottom: 8,
    letterSpacing: 0,
  },
  emptyTitleMobile: {
    maxWidth: '100%',
    paddingHorizontal: 16,
  },
  emptySubtitle: {
    fontSize: Platform.OS === 'web' ? 15 : 14,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: Platform.OS === 'web' ? 22 : 20,
    opacity: 0.7,
    maxWidth: 380,
    paddingHorizontal: 8,
  },
  emptySubtitleMobile: {
    fontSize: 12,
    lineHeight: 16,
    maxWidth: '100%',
    paddingHorizontal: 16,
  },

  // Error States
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },

  errorText: {
    marginTop: 12,
    fontSize: 16,
    textAlign: 'center',
  },

  floatingActionButton: {
    position: 'absolute',
    bottom: Platform.OS === 'web' ? 24 : 100,
    right: Platform.OS === 'web' ? 24 : 20,
    zIndex: 1000,
    elevation: Platform.OS === 'android' ? 8 : 0,
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
    }),
  },

  fabButton: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 8 : 0,
  },

  // New styles for privacy badge
  privacyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 10,
    gap: 3,
  },
  privacyText: {
    fontSize: 9,
    fontWeight: '600',
  },

  scrollableMoodBoards: {
    flex: 1,
    marginTop: Platform.OS === 'web' ? 240 : 0,
    marginBottom: 0,
  },
  scrollableMoodBoardsContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: Platform.OS === 'web' ? 100 : 120,
    width: '100%',
    maxWidth: '100%',
    alignContent: 'stretch',
    alignItems: 'stretch',
    flexGrow: 1,
  },

  webContentContainer: {
    flex: 1,
    marginTop: Platform.OS === 'web' ? 20 : 20,
    marginBottom: 0, // Remove bottom margin for free-flowing scroll
    ...(Platform.OS === 'web' && {
      position: 'absolute' as any,
      // top will be set dynamically via inline style
      left: 0,
      right: 0,
      bottom: 0, // Remove bottom constraint for free-flowing scroll
      overflow: 'hidden',
    }),
  },
  webScrollView: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      height: '100%',
    }),
  },
  webScrollContent: {
    paddingHorizontal: Platform.OS === 'web' ? 0 : 0,
    paddingTop: 6, // Add minimal top padding to prevent overlap with tabs
    paddingBottom: 100, // Add padding to content for FAB space
    width: '100%',
    maxWidth: '100%',
  },

  // Thumbnail grid styles
  thumbnailGridContainer: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    flexWrap: Platform.OS === 'web' ? 'wrap' : 'nowrap',
    paddingHorizontal: Platform.OS === 'web' ? 8 : 0,
    paddingTop: Platform.OS === 'web' ? 8 : 0,
    paddingBottom: Platform.OS === 'web' ? 8 : 16,
    gap: Platform.OS === 'web' ? 12 : 0,
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: '100%',
    alignContent: 'stretch',
  },

  thumbnailCardContainer: {
    ...(Platform.OS === 'web'
      ? { width: '33.333%', minWidth: 200, maxWidth: 300 }
      : { width: '100%', marginHorizontal: 0, minWidth: 0 }), // Full width for mobile like social media
    marginBottom: Platform.OS === 'web' ? 12 : 1,
    alignSelf: 'flex-start',
  },

  thumbnailCard: {
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    width: Platform.OS === 'web' ? 300 : undefined,
    minHeight: Platform.OS === 'web' ? 500 : 450,
    aspectRatio: Platform.OS === 'web' ? 0.75 : 0.8,
  },

  thumbnailImageContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: 'rgba(0,0,0,0.05)',
  },

  thumbnailImage: {
    width: '100%',
    height: '100%',
  },

  emptyThumbnailImage: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },

  thumbnailImageCountBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },

  thumbnailImageCountText: {
    color: palette.white,
    fontSize: 10,
    fontWeight: '600',
  },

  thumbnailLikedBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 12,
    padding: 4,
  },

  thumbnailNavButton: {
    position: 'absolute',
    left: 8,
    top: '50%',
    marginTop: -18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },

  thumbnailNavButtonRight: {
    left: 'auto',
    right: 8,
  },

  thumbnailOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255,0.98)',
    padding: 10,
    maxHeight: '60%',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },

  thumbnailMainContent: {
    flex: 1,
    minHeight: 0,
  },

  thumbnailBottomSection: {
    marginTop: 'auto',
    flexShrink: 0,
  },

  thumbnailColorsSectionFixed: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },

  thumbnailHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 6,
  },

  thumbnailUserInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 6,
  },

  thumbnailUserDetails: {
    flex: 1,
  },

  thumbnailUsername: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 2,
  },

  thumbnailTimestamp: {
    fontSize: 9,
    opacity: 0.7,
  },

  thumbnailPrivacyBadge: {
    padding: 4,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  thumbnailTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 0,
  },

  thumbnailColorsSection: {
    marginBottom: 6,
  },

  thumbnailColorPalette: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },

  thumbnailColorCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },

  thumbnailActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    paddingTop: 8,
    paddingBottom: 0,
    flexShrink: 0,
  },

  thumbnailActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 4,
  },

  thumbnailActionText: {
    fontSize: 9,
    fontWeight: '500',
  },

  // Report Modal Styles
  reportModalSubtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  reportReasonsList: {
    gap: 8,
    marginBottom: 24,
  },
  reportReasonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 12,
  },
  reportReasonText: {
    fontSize: 14,
    flex: 1,
  },
  reportModalActions: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
  reportModalCancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  reportModalCancelText: {
    fontSize: 14,
    fontWeight: '600',
  },
  reportModalSubmitButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  reportModalSubmitText: {
    fontSize: 14,
    fontWeight: '600',
  },

  // Loading more indicator
  loadingMoreContainer: {
    paddingVertical: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingMoreText: {
    fontSize: 14,
    opacity: 0.7,
  },

  // Thumbnail card wrapper for FlatList
  thumbnailCardWrapper: {
    ...(Platform.OS === 'web'
      ? { flex: 1, minWidth: 200, maxWidth: 300, marginHorizontal: 6 }
      : { width: '100%', marginHorizontal: 0, minWidth: 0 }),
    marginBottom: Platform.OS === 'web' ? 12 : 1,
  },
  thumbnailGridRow: {
    paddingHorizontal: Platform.OS === 'web' ? 8 : 0,
    justifyContent: 'flex-start',
  },

  // Skeleton container for tab transitions
  transitionSkeletonContainer: {
    flex: 1,
    width: '100%',
  },
});

export default MoodBoardScreen;
